import { UploadImageDocument } from "@/generated/graphql";
import { useAppSelector } from "@/store/hooks";
import { MediaUsageContextSchema } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";
import type { MediaInput } from "./handlers/types";

/**
 * Handles uploading a local image file and calling the provided handler
 * with the result — keeping local file drops consistent with dnd-from-media drops.
 *
 * Usage:
 *   const { handleFileDrop, isUploading } = useLocalImageUpload(
 *     (media) => handlers.addImage(media, containerId)
 *   );
 */
export function useLocalImageUpload(onUploaded: (media: MediaInput, containerId?: string) => void) {
  const [isUploading, setIsUploading] = useState(false);
  // Tags the Media House copy by the slide being edited. Null outside the slide editor
  // (the lesson generator's draft editor), where the server falls back.
  const slideTemplate = useAppSelector((state) => state.slideEditor.present.slide?.template);
  const [uploadImageMutation] = useMutation(UploadImageDocument);

  const handleFileDrop = async (file: File, containerId?: string) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }

    setIsUploading(true);
    toast.loading("Uploading image...", { id: "imageUploadToast" });

    try {
      const upload = await uploadImageMutation({
        variables: {
          file,
          folder: "media/image",
          usageContext: MediaUsageContextSchema.Values.slideEditor,
          slideTemplate,
        },
      });

      if (!upload.data || upload.error) throw new Error();

      const { id, url, filename } = upload.data.uploadImage;
      onUploaded({ id, src: url, filename }, containerId);
      toast.success("Image uploaded", { id: "imageUploadToast" });
    } catch (_error) {
      toast.error("Failed to upload image", { id: "imageUploadToast" });
    } finally {
      setIsUploading(false);
    }
  };

  return { handleFileDrop, isUploading };
}
